import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Subscription, catchError, forkJoin, from, map, mergeMap, of, toArray } from 'rxjs';

import { SupportApiService } from '../core/support-api.service';
import { VarAccessService } from '../auth/var-access.service';
import { LiveRefreshService } from '../core/live-refresh.service';
import { SupportNotificationService } from '../core/support-notification.service';
import { QueueGrantee, QueueSummary, TicketListFilters, TicketSummary } from '../core/models';
import { priorityBadgeClass, priorityLabel, slaBadgeClass, slaLabel, statusBadgeClass } from '../core/badge-maps';
import { FilterBarComponent } from './filter-bar.component';
import { DEFAULT_INBOX_STATUS, isResolvable } from '../core/ticket-status';

/** Parallel PATCHes per bulk resolve. Kept below the browser's 6-per-host HTTP/1.1
 *  cap, which the inbox's own live stream already uses one of. */
const BULK_RESOLVE_CONCURRENCY = 3;
/** Bulk assign shares the resolve cap, for the same reason. */
const BULK_ASSIGN_CONCURRENCY = BULK_RESOLVE_CONCURRENCY;

@Component({
  selector: 'app-queue-inbox',
  standalone: true,
  imports: [CommonModule, NgbTooltip, FilterBarComponent],
  template: `
    <div class="bt-page">
      <div class="bt-page__head">
        <h1 class="bt-page__title">Inbox</h1>
        <p class="bt-page__subtitle">Support tickets across every queue you can access</p>
      </div>

      <div class="inbox">
        <aside class="queue-rail bt-panel" aria-label="Queues" data-testid="queue-rail">
          <div class="queue-rail__head" ngbTooltip="Queues you hold a grant on. Each row shows new / SLA-breached counts.">Queues</div>
          @for (queue of queues; track queue.id) {
            <div class="queue-row" [class.is-active]="queue.id === activeQueueId"
                 [attr.data-testid]="'queue-row-' + queue.id"
                 tabindex="0" role="button" (click)="selectQueue(queue.id)" (keyup.enter)="selectQueue(queue.id)">
              <span class="queue-row__name">{{ queue.name }}</span>
              <span class="queue-row__counts">
                <span [attr.data-testid]="'queue-new-count-' + queue.id" class="bt-count bt-count--info"
                      ngbTooltip="New tickets in this queue">{{ queue.new_count }}</span>
                @if (queue.breached_count) {
                  <span [attr.data-testid]="'queue-breached-count-' + queue.id" class="bt-count bt-count--danger"
                        ngbTooltip="Tickets with a breached SLA">{{ queue.breached_count }}</span>
                } @else {
                  <span [attr.data-testid]="'queue-breached-count-' + queue.id" class="bt-count"
                        ngbTooltip="Tickets with a breached SLA">0</span>
                }
              </span>
            </div>
          } @empty {
            <div class="queue-rail__empty">No queues assigned</div>
          }
        </aside>

        <section class="inbox__main">
          <div class="inbox__toolbar">
            <app-filter-bar (filtersChange)="onFiltersChange($event)"></app-filter-bar>
            @if (notifications.unseen(); as unseen) {
              <button type="button" class="bt-count bt-count--info unseen-badge" data-testid="inbox-unseen-badge"
                      ngbTooltip="Tickets that arrived since you last looked. Click to dismiss."
                      (click)="dismissUnseen()">
                {{ unseen }} new
              </button>
            }
            @if (!pickingAssignee) {
              <button type="button" class="bt-btn bt-btn--secondary" data-testid="bulk-assign-button"
                      [disabled]="selected.size === 0 || bulkAssigning" (click)="openAssignPicker()"
                      ngbTooltip="Assign the selected tickets to one agent. You pick the agent and confirm before anything changes; each assignment is recorded in that ticket's History under your name">
                @if (bulkAssigning) { Assigning… } @else {
                  Assign selected@if (selected.size) { <span class="count-suffix">({{ selected.size }})</span> }
                }
              </button>
            } @else {
              <span class="bulk-confirm" data-testid="bulk-assign-picker">
                <select class="bt-select" data-testid="bulk-assign-select" aria-label="Agent to assign the selected tickets to"
                        [disabled]="rosterLoading || !assignableAgents.length" (change)="onAssigneeChange($event)"
                        ngbTooltip="Agents who hold a grant on every queue the selected tickets sit in">
                  <option value="" [selected]="!assigneeId">{{ rosterLoading ? 'Loading agents…' : 'Choose an agent…' }}</option>
                  @for (agent of assignableAgents; track agent.id) {
                    <option [value]="agent.id" [selected]="agent.id === assigneeId">{{ agent.email }}</option>
                  }
                </select>
                @if (!rosterLoading && !assignableAgents.length) {
                  <span class="bulk-hint" data-testid="bulk-assign-empty">No agent has access to all selected queues</span>
                }
                <button type="button" class="bt-btn bt-btn--primary bt-btn--sm" data-testid="bulk-assign-confirm-button"
                        [disabled]="rosterLoading || !assigneeId" (click)="confirmBulkAssign()"
                        ngbTooltip="Assign the selected tickets to this agent now">Assign</button>
                <button type="button" class="bt-btn bt-btn--secondary bt-btn--sm" data-testid="bulk-assign-cancel"
                        (click)="closeAssignPicker()" ngbTooltip="Change nothing">Cancel</button>
              </span>
            }
            @if (!confirmingResolve) {
              <button type="button" class="bt-btn bt-btn--secondary" data-testid="bulk-resolve-button"
                      [disabled]="resolvableSelected().length === 0 || bulkResolving" (click)="confirmingResolve = true"
                      ngbTooltip="Resolve the selected tickets that are new, active or pending. You confirm before anything changes, and each resolve is recorded in that ticket's History under your name">
                @if (bulkResolving) { Resolving… } @else {
                  Resolve selected@if (resolvableSelected().length) { <span class="count-suffix">({{ resolvableSelected().length }})</span> }
                }
              </button>
            } @else {
              <span class="bulk-confirm" data-testid="bulk-resolve-confirm">
                <span>Resolve {{ resolvableSelected().length }} ticket(s)?</span>
                <button type="button" class="bt-btn bt-btn--primary bt-btn--sm" data-testid="bulk-resolve-confirm-button"
                        (click)="confirmBulkResolve()" ngbTooltip="Resolve them now">Resolve</button>
                <button type="button" class="bt-btn bt-btn--secondary bt-btn--sm" data-testid="bulk-resolve-cancel"
                        (click)="confirmingResolve = false" ngbTooltip="Change nothing">Cancel</button>
              </span>
            }
          </div>
          @if (assignResult) {
            <div class="bulk-result" data-testid="bulk-assign-result" role="status">{{ assignResult }}</div>
          }
          @if (bulkResult) {
            <div class="bulk-result" data-testid="bulk-resolve-result" role="status">{{ bulkResult }}</div>
          }

          @if (loading) {
            <div class="bt-empty"><span class="bt-spinner"></span><span class="bt-empty__hint">Loading tickets…</span></div>
          } @else if (!tickets.length) {
            <div class="bt-empty" data-testid="inbox-empty">
              <span class="bt-empty__icon">📭</span>
              <span class="bt-empty__title">No tickets match</span>
              <span class="bt-empty__hint">Nothing in your queues for the current filters.</span>
            </div>
          } @else {
            <table class="bt-table" aria-label="Tickets" data-testid="ticket-table">
              <thead>
                <tr>
                  <th class="col-check" ngbTooltip="Select every ticket shown, for a bulk assign or resolve">
                    <input type="checkbox" data-testid="select-all" aria-label="Select all tickets shown"
                           [checked]="allSelected()" (change)="toggleSelectAll($event)" />
                  </th>
                  <th class="col-ref" ngbTooltip="System-assigned ticket reference">Reference</th>
                  <th ngbTooltip="What the requester reported">Subject</th>
                  <th class="col-status" ngbTooltip="Current stage of the ticket">Status</th>
                  <th class="col-prio" ngbTooltip="Priority — drives the SLA clock; a dot marks an agent override">Priority</th>
                  <th class="col-sla" ngbTooltip="Whether the first-response SLA is on track, at risk, or breached">First&nbsp;response</th>
                </tr>
              </thead>
              <tbody>
                @for (ticket of tickets; track ticket.id) {
                  <tr class="is-clickable" [attr.data-testid]="'ticket-row-' + ticket.id" tabindex="0"
                      [class.is-new]="newTicketIds.has(ticket.id)"
                      [attr.aria-label]="newTicketIds.has(ticket.id) ? 'New since you last looked: ' + ticket.reference : null"
                      (click)="openTicket(ticket.id)" (keyup.enter)="openTicket(ticket.id)">
                    <td class="col-check">
                      <input type="checkbox" [attr.data-testid]="'select-' + ticket.id"
                             [attr.aria-label]="'Select ticket ' + ticket.reference" [checked]="selected.has(ticket.id)"
                             (click)="$event.stopPropagation()" (change)="toggleSelect(ticket.id, $event)" />
                    </td>
                    <td class="col-ref"><span class="ref">{{ ticket.reference }}</span></td>
                    <td class="subject">{{ ticket.subject }}</td>
                    <td class="col-status"><span class="bt-badge" [class]="statusClass(ticket.status)">{{ ticket.status }}</span></td>
                    <td class="col-prio">
                      <span class="bt-badge" [class]="priorityClass(ticket.priority)">{{ priorityLabel(ticket.priority) }}</span>
                      @if (ticket.priority_source === 'agent_override') {
                        <span class="override-dot" [attr.data-testid]="'priority-override-indicator-' + ticket.id"
                              ngbTooltip="Overridden by an agent from the derived value">•</span>
                      }
                    </td>
                    <td class="col-sla">
                      <span class="bt-badge" [class]="slaClass(ticket.first_response_sla_state)"
                            [attr.data-testid]="'sla-badge-' + ticket.id">{{ slaLabel(ticket.first_response_sla_state) }}</span>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </section>
      </div>
    </div>
  `,
  styles: [`
    .inbox { display: flex; gap: 1.25rem; align-items: flex-start; }
    .queue-rail { width: 240px; flex: 0 0 auto; padding: 0.5rem; position: sticky; top: 1.5rem; }
    .queue-rail__head {
      padding: 0.5rem 0.7rem; font-size: 11px; font-weight: 700; text-transform: uppercase;
      letter-spacing: 0.06em; color: var(--bt-text-muted);
    }
    .queue-rail__empty { padding: 0.6rem 0.7rem; font-size: 13px; color: var(--bt-text-muted); }
    .queue-row {
      display: flex; align-items: center; gap: 0.5rem; padding: 0.55rem 0.7rem; border-radius: 8px; cursor: pointer;
      transition: background-color .12s ease;
    }
    .queue-row:hover { background: var(--bt-bg-surface-2); }
    .queue-row.is-active { background: color-mix(in srgb, var(--bt-brand-intercept) 12%, transparent); }
    .queue-row__name { flex: 1; font-size: 14px; color: var(--bt-text-primary); font-weight: 500; }
    .queue-row__counts { display: flex; gap: 0.3rem; }

    .inbox__main { flex: 1; min-width: 0; }
    .inbox__toolbar { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.5rem; flex-wrap: wrap; }
    .inbox__toolbar app-filter-bar { flex: 1; min-width: 0; }
    .count-suffix { margin-left: 0.3rem; opacity: 0.8; }
    .unseen-badge { border: none; cursor: pointer; font: inherit; }
    .bulk-confirm { display: inline-flex; align-items: center; gap: 0.5rem; font-size: 14px; color: var(--bt-text-primary); }
    .bulk-hint { font-size: 13px; color: var(--bt-text-muted); }
    .bulk-result { font-size: 13px; color: var(--bt-text-secondary); margin-bottom: 0.5rem; }

    /* A newly-arrived row (AB#1884). Colour is only ONE of the two cues — the row
       also carries an aria-label naming it as new, so the highlight is never the
       sole carrier of the information. */
    tr.is-new { background: color-mix(in srgb, var(--bt-brand-intercept) 10%, transparent); }
    tr.is-new .ref::before {
      content: '●'; color: var(--bt-brand-intercept); font-size: 10px;
      margin-right: 0.35rem; vertical-align: middle;
    }

    .col-check { width: 34px; }
    .col-ref { width: 120px; }
    .col-status, .col-prio { width: 118px; }
    .col-sla { width: 130px; }
    .ref { font-family: 'Roboto Mono', ui-monospace, monospace; font-size: 13px; color: var(--bt-text-secondary); font-weight: 600; }
    .subject { color: var(--bt-text-primary); font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .override-dot { color: var(--bt-status-warning); font-size: 18px; line-height: 0; margin-left: 3px; vertical-align: middle; }
    input[type=checkbox] { width: 15px; height: 15px; accent-color: var(--bt-brand-intercept); cursor: pointer; }
  `],
})
export class QueueInboxComponent implements OnInit {
  private api = inject(SupportApiService);
  private router = inject(Router);
  private live = inject(LiveRefreshService);
  private varAccess = inject(VarAccessService);
  private destroyRef = inject(DestroyRef);
  /** Public: the template renders its unseen count as a badge. */
  notifications = inject(SupportNotificationService);

  queues: QueueSummary[] = [];
  tickets: TicketSummary[] = [];
  selected = new Set<string>();
  loading = true;
  confirmingResolve = false;
  bulkResolving = false;
  bulkResult: string | null = null;
  pickingAssignee = false;
  rosterLoading = false;
  bulkAssigning = false;
  assignableAgents: QueueGrantee[] = [];
  assigneeId = '';
  assignResult: string | null = null;
  /** In-flight roster request, cancelled whenever the picker closes. */
  private rosterSub: Subscription | null = null;
  /** Queues the open roster was built for. */
  private rosterQueueIds = new Set<string>();
  activeQueueId: string | null = null;
  /** Ids that appeared in a BACKGROUND refresh — highlighted so the agent can see
   *  what changed without diffing the list themselves. */
  newTicketIds = new Set<string>();
  private currentFilters: TicketListFilters = { status: DEFAULT_INBOX_STATUS };
  /** Every id this component has rendered, ever. The arrival diff is against THIS,
   *  not against the previous page of `tickets`, so a filter change (or a ticket
   *  scrolling out of and back into the current filter) never re-reports an old
   *  ticket as a new arrival. */
  private knownTicketIds = new Set<string>();

  ngOnInit(): void {
    this.api.listQueues().subscribe((queues) => (this.queues = queues));
    this.reload();
    // AB#1866: the inbox refreshes itself. `null` scope = every ticket this agent can
    // read, which is exactly what the inbox lists.
    //
    // STAFF ONLY, and this is a security constraint rather than a product choice
    // (security-check HIGH, PR #1571). `GET /api/v1/tickets` admits `var` and scopes by
    // `support.queue_grants` ALONE — `routers/tickets.py::list_tickets_endpoint` never
    // applies `core.var_access.reachable()` / `authorized_org_ids` / `bt_only` /
    // `active_until`, though `core/event_reads.py` applies exactly those for the VAR
    // STREAM scope and says in as many words that the grant join alone would otherwise
    // expose cross-org, expired, and escalated-to-bt_only tickets on a granted queue.
    // Every VAR principal is provisioned with a live read grant on `l1-general`
    // (repositories/var_principals.py), and `inboxAccessGuard` admits a CERTIFIED VAR —
    // so arming this poll unconditionally would turn a once-per-navigation read of that
    // gap into a continuous 3s harvest.
    //
    // The gap is PRE-EXISTING and server-side; this PR changes no `bluetitan-support`
    // file. What it must not do is AMPLIFY it. So the auto-refresh is armed for staff
    // only until the server-side scoping lands (filed separately), which means a VAR
    // keeps today's manual-reload behaviour on the inbox rather than gaining an
    // automated read of an under-scoped endpoint. The ticket-detail poll needs no such
    // guard: its endpoints (`/events`, `/messages`) both resolve VAR scope through
    // `list_streamable_ticket_ids` / `resolve_ticket_access`, which DO apply reachable().
    if (this.varAccess.isStaff()) {
      this.live.watch(null)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.backgroundRefresh());
    }
  }

  reload(): void {
    this.loading = true;
    this.api.listTickets(this.currentFilters).subscribe({
      next: (tickets) => { this.applyTickets(tickets, { announce: false }); this.loading = false; },
      error: () => { this.tickets = []; this.loading = false; },
    });
  }

  /**
   * A refresh the agent did not ask for. Two differences from `reload()` that matter:
   * it never raises `loading` (a spinner replacing the table every few seconds while
   * the agent is reading it would be worse than the stale list it fixes), and a
   * failure leaves the last good list on screen rather than blanking it — a dropped
   * poll is not evidence the queue is empty.
   */
  private backgroundRefresh(): void {
    this.api.listTickets(this.currentFilters).subscribe({
      next: (tickets) => this.applyTickets(tickets, { announce: true }),
      error: () => { /* keep what is on screen; the next tick retries */ },
    });
    // The queue rail's new/breached counts must refresh WITH the table. Loading them
    // only in ngOnInit left two counts of the same thing on one screen, one of them
    // frozen: the rail said "3 new" for the life of the page while five more tickets
    // arrived and were highlighted in the table beside it. (code-review, PR #1571.)
    // Same quiet-failure rule — a dropped poll keeps the last good counts.
    this.api.listQueues().subscribe({
      next: (queues) => (this.queues = queues),
      error: () => { /* keep what is on screen */ },
    });
  }

  private applyTickets(tickets: TicketSummary[], opts: { announce: boolean }): void {
    const arrived = tickets.filter((t) => !this.knownTicketIds.has(t.id));
    for (const t of tickets) this.knownTicketIds.add(t.id);
    this.tickets = tickets;
    // Selection only ever covers rows on screen, so a bulk action can never reach a
    // ticket the agent cannot see (one that left the current filter, or got resolved).
    const shown = new Set(tickets.map((t) => t.id));
    for (const id of [...this.selected]) if (!shown.has(id)) { this.selected.delete(id); this.closeAssignPicker(); }
    if (this.pickingAssignee && !this.rosterCoversSelection()) this.closeAssignPicker();
    if (!opts.announce || arrived.length === 0) return;
    for (const t of arrived) this.newTicketIds.add(t.id);
    this.notifications.noteTickets(arrived.length);
  }

  onFiltersChange(filters: TicketListFilters): void {
    this.currentFilters = filters;
    this.reload();
  }

  /** The agent acknowledged the badge without opening anything. */
  dismissUnseen(): void {
    this.notifications.clear();
    this.newTicketIds.clear();
  }

  selectQueue(queueId: string): void {
    // Visual selection only — queue scoping happens server-side (GET /tickets already
    // scopes to every queue the principal holds a grant on); a future iteration can add
    // a queue_id filter param here once the backend exposes one.
    this.activeQueueId = this.activeQueueId === queueId ? null : queueId;
  }

  openTicket(ticketId: string): void {
    // Opening a ticket IS the agent having looked — drop the badge and the
    // row highlights rather than leaving a stale "N new" behind them.
    this.dismissUnseen();
    this.router.navigate(['/tickets', ticketId]);
  }

  allSelected(): boolean {
    return this.tickets.length > 0 && this.tickets.every((t) => this.selected.has(t.id));
  }

  toggleSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    for (const t of this.tickets) {
      if (checked) this.selected.add(t.id); else this.selected.delete(t.id);
    }
    this.closeAssignPicker();
  }

  /** Selected rows a Resolve would actually change; resolved/closed ones are skipped. */
  resolvableSelected(): TicketSummary[] {
    return this.tickets.filter((t) => this.selected.has(t.id) && isResolvable(t.status));
  }

  /**
   * One PATCH per ticket through the same `target_status` path as the ticket page's
   * Resolve button, so each one is authorized, validated and recorded per ticket on
   * the server. A failure is reported by reference and leaves that ticket selected
   * for a retry, and never stops the others.
   */
  confirmBulkResolve(): void {
    const targets = this.resolvableSelected();
    this.confirmingResolve = false;
    if (targets.length === 0) return;
    this.bulkResolving = true;
    this.bulkResult = null;
    from(targets).pipe(
      mergeMap((t) => this.api.patchTicket(t.id, { target_status: 'resolved' }).pipe(
        map(() => ({ ticket: t, ok: true })),
        catchError(() => of({ ticket: t, ok: false })),
      ), BULK_RESOLVE_CONCURRENCY),
      toArray(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((results) => {
      const failed = results.filter((r) => !r.ok).map((r) => r.ticket.reference);
      for (const r of results) if (r.ok) this.selected.delete(r.ticket.id);
      const done = results.length - failed.length;
      this.bulkResolving = false;
      this.bulkResult = failed.length
        ? `Resolved ${done} of ${results.length}. Not resolved: ${failed.join(', ')}. They may have changed; reload and retry.`
        : `Resolved ${done} ticket(s).`;
      this.reload();
    });
  }

  /** Selected rows, in list order. */
  private selectedTickets(): TicketSummary[] {
    return this.tickets.filter((t) => this.selected.has(t.id));
  }

  /**
   * Opens the inline picker. The offered agents are the grantees of EVERY queue the
   * selected tickets sit in (intersection), so one choice is valid for all of them.
   * A failed roster fetch degrades to the same empty state as "nobody qualifies".
   * The picker closes whenever the selection changes, and closing cancels any roster
   * request still in flight, so a late response can never describe a different set of
   * tickets than the one the Assign would hit. The server does not check that the
   * assignee holds a grant on the ticket's queue, so this client check is what keeps
   * a bulk assign inside each ticket's queue.
   */
  openAssignPicker(): void {
    const queueIds = [...new Set(this.selectedTickets().map((t) => t.queue_id))];
    if (queueIds.length === 0) return;
    this.rosterSub?.unsubscribe();
    this.pickingAssignee = true;
    this.rosterLoading = true;
    this.assignableAgents = [];
    this.assigneeId = '';
    this.assignResult = null;
    this.rosterQueueIds = new Set(queueIds);
    this.rosterSub = forkJoin(queueIds.map((q) => this.api.listQueueGrantees(q))).pipe(
      map((rosters) => rosters.reduce<QueueGrantee[]>(
        (common, roster) => common.filter((a) => roster.some((r) => r.id === a.id)),
        rosters[0],
      )),
      catchError(() => of<QueueGrantee[]>([])),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((agents) => {
      this.assignableAgents = agents;
      this.rosterLoading = false;
    });
  }

  /** True when every selected ticket still sits in a queue the open roster was built for. */
  private rosterCoversSelection(): boolean {
    return this.selectedTickets().every((t) => this.rosterQueueIds.has(t.queue_id));
  }

  closeAssignPicker(): void {
    this.rosterSub?.unsubscribe();
    this.rosterSub = null;
    this.rosterQueueIds = new Set();
    this.pickingAssignee = false;
    this.rosterLoading = false;
    this.assignableAgents = [];
    this.assigneeId = '';
  }

  onAssigneeChange(event: Event): void {
    this.assigneeId = (event.target as HTMLSelectElement).value;
  }

  /**
   * One PATCH per ticket with `assignee_principal_id`, the same path as the ticket
   * page's Assign control, so the server still authorizes each one (e.g. a VAR assignee
   * must be certified). A failure is reported by reference, leaves that ticket selected
   * for a retry, and never stops the others.
   */
  confirmBulkAssign(): void {
    const agent = this.assignableAgents.find((a) => a.id === this.assigneeId);
    const targets = this.selectedTickets();
    if (!agent || targets.length === 0) return;
    if (!this.rosterCoversSelection()) {
      // A refresh moved a selected ticket to a queue this roster never covered.
      this.closeAssignPicker();
      this.assignResult = 'A selected ticket moved to another queue. Open Assign selected again to pick from the current agents.';
      return;
    }
    this.closeAssignPicker();
    this.bulkAssigning = true;
    this.assignResult = null;
    this.bulkResult = null;
    from(targets).pipe(
      mergeMap((t) => this.api.patchTicket(t.id, { assignee_principal_id: agent.id }).pipe(
        map(() => ({ ticket: t, ok: true })),
        catchError(() => of({ ticket: t, ok: false })),
      ), BULK_ASSIGN_CONCURRENCY),
      toArray(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((results) => {
      const failed = results.filter((r) => !r.ok).map((r) => r.ticket.reference);
      for (const r of results) if (r.ok) this.selected.delete(r.ticket.id);
      const done = results.length - failed.length;
      this.bulkAssigning = false;
      this.assignResult = failed.length
        ? `Assigned ${done} of ${results.length}. Not assigned: ${failed.join(', ')}. They may have changed, or the agent may not be eligible; reload and retry.`
        : `Assigned ${done} ticket(s) to ${agent.email}.`;
      this.reload();
    });
  }

  toggleSelect(ticketId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) this.selected.add(ticketId); else this.selected.delete(ticketId);
    this.closeAssignPicker();
  }

  slaLabel = slaLabel;
  priorityLabel = priorityLabel;
  statusClass = statusBadgeClass;
  priorityClass = priorityBadgeClass;
  slaClass = slaBadgeClass;
}
